<template>
    <nav class="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div class="mx-auto w-full max-w-6xl px-4 md:px-6">
            <div class="relative flex h-16 items-center justify-between">
                <!-- Mobile menu button -->
                <div class="flex items-center md:hidden">
                    <!-- modal=false: 드로어 열 때 body 스크롤 잠금·스크롤바 보정 padding 제거 (우측 갭 방지) -->
                    <Sheet v-model:open="mobileMenuOpen" :modal="false">
                        <SheetTrigger as-child>
                            <Button variant="ghost" size="icon" class="h-9 w-9">
                                <Menu class="h-5 w-5" />
                                <span class="sr-only">Toggle navigation</span>
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" class="w-72">
                            <SheetHeader>
                                <SheetTitle>Menu</SheetTitle>
                            </SheetHeader>
                            <nav v-if="authStore.isAuthenticated" class="mt-6 flex flex-col space-y-4">
                                <!-- 내정보 — 하위 항목이 /manage 내부 탭으로 통합됨 → 단일 링크 -->
                                <router-link to="/manage" class="flex items-center min-h-[44px] px-3 -mx-1 rounded-lg text-[15px] font-semibold hover:bg-accent hover:text-primary active:bg-accent" :class="isMyInfoActive ? 'text-primary' : 'text-foreground'" @click="mobileMenuOpen = false">{{ $t('layout.header.dropdown.myInfo') }}</router-link>
                                <!-- 고객센터는 상단에서 제거 → 푸터로 이동 (2026-07-30) -->
                                <!-- 패밀리 사이트 (모바일) — 권한 매칭된 사이트가 있을 때만 -->
                                <div v-if="familySitesAvailable.length > 0" class="space-y-4">
                                    <p class="px-3 -mx-1 text-sm font-semibold text-muted-foreground">{{ $t('layout.header.dropdown.familySites') }}</p>
                                    <div v-for="group in familySiteGroups" :key="group.code" class="space-y-0.5">
                                        <p class="px-3 -mx-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                            {{ group.name }}
                                        </p>
                                        <a v-for="site in group.sites"
                                           :key="site.siteCode"
                                           :href="site.siteUrl"
                                           :target="site.openInNewTab !== false ? '_blank' : '_self'"
                                           :rel="site.openInNewTab !== false ? 'noopener noreferrer' : undefined"
                                           class="flex items-center min-h-[44px] px-3 -mx-1 rounded-lg text-[15px] text-foreground hover:bg-accent hover:text-primary active:bg-accent"
                                           @click="mobileMenuOpen = false">
                                            {{ site.displayName }}
                                        </a>
                                    </div>
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>

                <!-- Logo and desktop menu -->
                <div class="flex flex-1 items-center justify-center md:items-stretch md:justify-start">
                    <router-link to="/" class="flex flex-shrink-0 items-center" aria-label="WMD PASS Home">
                        <img :src="logoPath" class="h-8 md:h-9 w-auto block dark:hidden" alt="WMD PASS" />
                        <img :src="logoDarkPath" class="h-8 md:h-9 w-auto hidden dark:block" alt="WMD PASS" />
                    </router-link>

                    <div v-if="authStore.isAuthenticated" class="hidden md:ml-10 md:flex items-center">
                        <ul class="flex items-center space-x-1">
                            <!-- 내정보 — 하위 항목이 /manage 내부 탭으로 통합됨 → 단일 링크 -->
                            <li>
                                <Button as-child variant="ghost" class="h-9 px-3" :class="{ 'text-primary font-semibold': isMyInfoActive }">
                                    <router-link to="/manage">{{ $t('layout.header.dropdown.myInfo') }}</router-link>
                                </Button>
                            </li>
                            <!-- '로그인 설정' → 프로필 메뉴로 이동, '고객센터' → 푸터로 이동 (2026-07-30) -->
                            <!-- 패밀리 사이트 — 권한 매칭된 사이트가 있을 때만 표시 -->
                            <li v-if="familySitesAvailable.length > 0">
                                <DropdownMenu :modal="false">
                                    <DropdownMenuTrigger as-child>
                                        <Button variant="ghost" class="h-9 px-3">
                                            {{ $t('layout.header.dropdown.familySites') }}
                                            <ChevronDown class="ml-1 h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <!-- 홈의 "전체 서비스" 와 같은 분류로 묶는다. 서비스가 늘어나면서
                                         한 덩어리 목록으로는 원하는 것을 찾기 어려워졌다. -->
                                    <DropdownMenuContent align="start" class="max-h-[70vh] w-60 overflow-y-auto">
                                        <template v-for="(group, gi) in familySiteGroups" :key="group.code">
                                            <DropdownMenuSeparator v-if="gi > 0" />
                                            <DropdownMenuLabel class="text-xs text-muted-foreground">
                                                {{ group.name }}
                                            </DropdownMenuLabel>
                                            <DropdownMenuItem v-for="site in group.sites" :key="site.siteCode" as-child>
                                                <a :href="site.siteUrl"
                                                   :target="site.openInNewTab !== false ? '_blank' : '_self'"
                                                   :rel="site.openInNewTab !== false ? 'noopener noreferrer' : undefined"
                                                   class="flex items-center w-full">
                                                    <ExternalLink class="mr-2 h-4 w-4" />
                                                    <span class="flex-1 truncate">{{ site.displayName }}</span>
                                                </a>
                                            </DropdownMenuItem>
                                        </template>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Right side -->
                <div class="flex items-center space-x-2">
                    <!-- 다크모드/언어는 프로필 드롭다운 안으로 통합 — 상단 독립 스위치 제거 (2026-07-30) -->
                    <DropdownMenu v-if="authStore.isAuthenticated" :modal="false">
                        <DropdownMenuTrigger as-child>
                            <Button variant="ghost" size="icon" class="h-10 w-10 rounded-full p-0 overflow-hidden">
                                <!-- 프로필 이미지가 없으면 이니셜 텍스트 (사용자 의도: 안면인식 사진과 분리) -->
                                <div
                                    v-if="!profileImage"
                                    class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-base font-bold"
                                >
                                    {{ userInitial }}
                                </div>
                                <img v-else class="w-10 h-10 object-cover" :src="profileImage" alt="User Profile">
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="w-56">
                            <DropdownMenuLabel>
                                <div class="flex flex-col space-y-1">
                                    <p class="text-sm font-medium">{{ authStore.userName }}</p>
                                    <p class="text-xs text-muted-foreground truncate">{{ authStore.userEmail }}</p>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem as-child><router-link to="/manage" class="w-full"><User class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.myInfo') }}</router-link></DropdownMenuItem>
                            <DropdownMenuItem @click="showProfileImageEditor = true" class="cursor-pointer"><Image class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.profileImage') }}</DropdownMenuItem>
                            <DropdownMenuItem as-child><router-link to="/manage/changepassword" class="w-full"><Lock class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.changePassword') }}</router-link></DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <!-- '로그인 설정' 항목 이관 (2026-07-30) -->
                            <DropdownMenuItem as-child><router-link to="/manage/twofactorauthentication" class="w-full"><ShieldCheck class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.twoFactorAuth') }}</router-link></DropdownMenuItem>
                            <DropdownMenuItem as-child><router-link to="/manage/externallogins" class="w-full"><LinkIcon class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.snsAuth') }}</router-link></DropdownMenuItem>
                            <DropdownMenuItem as-child><router-link to="/grants" class="w-full"><ShieldCheck class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.sitePermission') }}</router-link></DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <!-- 다크/라이트 모드 토글 (헤더 아이콘과 상태 공유) -->
                            <DropdownMenuItem class="cursor-pointer" @select.prevent="toggleDarkMode">
                                <Sun v-if="isDark" class="mr-2 h-4 w-4" />
                                <Moon v-else class="mr-2 h-4 w-4" />
                                {{ isDark ? ($t('layout.header.dropdown.lightMode') || '라이트 모드') : ($t('layout.header.dropdown.darkMode') || '다크 모드') }}
                            </DropdownMenuItem>
                            <!-- 언어 선택 (상단 독립 셀렉터에서 이관) -->
                            <LangMenuSub />
                            <DropdownMenuSeparator />
                            <!-- 회원 탈퇴는 프로필 메뉴에서 제거 (2026-07-30) — /manage/leave 경로는 유지 -->
                            <DropdownMenuItem @click="handleLogout" class="cursor-pointer"><LogOut class="mr-2 h-4 w-4" />{{ $t('layout.header.dropdown.logout') }}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>
    </nav>

    <!-- 프로필 이미지 변경 모달 -->
    <ProfileImageEditorModal v-model="showProfileImageEditor" />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@stores/auth';
import multiavatar from '@multiavatar/multiavatar/esm';
import LangMenuSub from './LangMenuSub.vue';
import ProfileImageEditorModal from './ProfileImageEditorModal.vue';
import api from '@/api';
import { Menu, ChevronDown, User, Lock, LogOut, Image, ExternalLink, ShieldCheck, Link as LinkIcon, Sun, Moon } from 'lucide-vue-next';
import { useDarkMode } from '@/composables/useDarkMode';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import logoImage from '../../../assets/images/wmdpass-logo.png';
import logoDarkImage from '../../../assets/images/wmdpass-logo-dark.png';

const router = useRouter();
const route = useRoute();
const { locale, t } = useI18n();
const authStore = useAuthStore();
const { isDark, toggleDarkMode } = useDarkMode();

// 패밀리 사이트 (auth store에서 가져옴) — siteUrl 있는 항목만 메뉴에 노출
const familySitesAvailable = computed(() =>
    (authStore.familySites || []).filter(s => s.siteUrl)
);

// 메뉴도 홈의 "전체 서비스" 와 같은 분류로 묶는다.
// 서버가 분류 순서대로 내려주므로 등장 순서를 그대로 쓴다(미분류는 "기타" 로 맨 뒤).
const familySiteGroups = computed(() => {
    const map = new Map();
    for (const site of familySitesAvailable.value) {
        const code = site.categoryCode || '__etc__';
        if (!map.has(code)) {
            map.set(code, {
                code,
                name: site.categoryName || t('familySites.etc'),
                order: site.categorySortOrder ?? 9999,
                sites: [],
            });
        }
        map.get(code).sites.push(site);
    }
    return [...map.values()].sort((a, b) => a.order - b.order);
});

// 프로필 이미지는 스토어에 보관 → 사진 변경/삭제 시 어느 페이지에서든 갱신 가능
const profileImage = computed(() => authStore.profileImage);
const mobileMenuOpen = ref(false);
const showProfileImageEditor = ref(false);

// 대메뉴 활성화 상태 계산
const isMyInfoActive = computed(() => {
    const path = route.path;
    // /manage 또는 /account/proxy-family — 단, 로그인 설정 관련 경로는 제외
    if (path.startsWith('/account/proxy-family')) return true;
    return path.startsWith('/manage') &&
           !path.startsWith('/manage/twofactorauthentication') &&
           !path.startsWith('/manage/externallogins');
});

const isLoginSettingActive = computed(() => {
    const path = route.path;
    return path.startsWith('/manage/twofactorauthentication') ||
           path.startsWith('/manage/externallogins') ||
           path.startsWith('/grants');
});

const isCustomerServiceActive = computed(() => {
    return route.path.startsWith('/cs');
});

// 프로필 이미지가 없을 때 표시할 이니셜 (userName 또는 이메일의 첫 글자, 대문자)
const userInitial = computed(() => {
    const name = authStore.userName || authStore.userEmail || '';
    return name ? name.charAt(0).toUpperCase() : '?';
});

const logoPath = logoImage;
const logoDarkPath = logoDarkImage;

async function handleLogout() {
    try {
        const response = await api.post('/api/account/logout', { logoutId: '' });
        authStore.logout();
        if (response.data && response.data.redirectUrl) {
            router.push(response.data.redirectUrl);
        } else {
            router.push('/account/loggedout');
        }
    } catch (error) {
        console.error('Logout failed:', error);
        authStore.logout();
        router.push('/account/loggedout');
    }
}

// 인증 상태가 준비되면 사진 + 패밀리 사이트 로드 (앱 마운트 시점엔 아직 미인증일 수 있어 watch로 처리)
watch(
    () => authStore.isAuthenticated,
    (isAuth) => {
        if (isAuth) {
            authStore.loadProfileImage();
            authStore.loadFamilySites(locale.value);
        }
    },
    { immediate: true }
);

// i18n locale 변경 시 패밀리 사이트 응답 다국어 갱신
watch(locale, (newLocale) => {
    if (authStore.isAuthenticated) {
        authStore.loadFamilySites(newLocale);
    }
});
</script>
